const CHANNEL_NAME = 'proom-tables';
const STORAGE_KEY = 'proom-tables-event';
const RADIX_36 = 36;
const RANDOM_SUFFIX_LENGTH = 2;
const TAB_ID = `${Date.now()}-${Math.random().toString(RADIX_36).slice(RANDOM_SUFFIX_LENGTH)}`;

export type ProomTableCoordinationMessage =
    | {boardId: string; tabId: string; type: 'claim'}
    | {boardId: string; tabId: string; type: 'release'}
    | {boardId: string; externalId: string; tabId: string; type: 'restore'};

const remoteClaims = new Map<string, string>();
const localClaims = new Set<string>();

let channel: BroadcastChannel | null = null;
const subscribers = new Set<(message: ProomTableCoordinationMessage) => void>();

const initChannel = (): BroadcastChannel | null => {
    if (typeof BroadcastChannel === 'undefined') {
        return null;
    }

    if (!channel) {
        channel = new BroadcastChannel(CHANNEL_NAME);
        channel.onmessage = (event: MessageEvent<ProomTableCoordinationMessage>) => {
            handleCoordinationMessage(event.data);
        };
    }

    return channel;
};

const handleCoordinationMessage = (message: ProomTableCoordinationMessage): void => {
    if (message.tabId === TAB_ID) {
        return;
    }

    if (message.type === 'claim') {
        remoteClaims.set(message.boardId, message.tabId);
    }

    if (message.type === 'release') {
        const claimTabId = remoteClaims.get(message.boardId);

        if (claimTabId === message.tabId) {
            remoteClaims.delete(message.boardId);
        }
    }

    subscribers.forEach(handler => handler(message));
};

const publishMessage = (message: ProomTableCoordinationMessage): void => {
    const activeChannel = initChannel();

    if (activeChannel) {
        activeChannel.postMessage(message);
        return;
    }

    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({...message, timestamp: Date.now()}));
    } catch {
        // LocalStorage may be unavailable in private mode
    }
};

const handleStorageEvent = (event: StorageEvent): void => {
    if (event.key !== STORAGE_KEY || !event.newValue) {
        return;
    }

    try {
        const message = JSON.parse(event.newValue) as ProomTableCoordinationMessage;

        if (message.tabId === TAB_ID) {
            return;
        }

        handleCoordinationMessage(message);
    } catch {
        // Ignore malformed storage events
    }
};

const releaseAllLocalClaims = (): void => {
    localClaims.forEach(boardId => {
        publishMessage({boardId, tabId: TAB_ID, type: 'release'});
    });
    localClaims.clear();
};

const handlePageHide = (event: PageTransitionEvent): void => {
    if (event.persisted) {
        return;
    }

    releaseAllLocalClaims();
};

if (typeof window !== 'undefined') {
    window.addEventListener('storage', handleStorageEvent);
    window.addEventListener('pagehide', handlePageHide);
}

export const claimBoardId = (boardId: string): void => {
    localClaims.add(boardId);
    remoteClaims.delete(boardId);
    publishMessage({boardId, tabId: TAB_ID, type: 'claim'});
};

export const releaseBoardId = (boardId: string): void => {
    localClaims.delete(boardId);

    if (remoteClaims.get(boardId) === TAB_ID) {
        remoteClaims.delete(boardId);
    }

    publishMessage({boardId, tabId: TAB_ID, type: 'release'});
};

export const requestRestoreTable = (boardId: string, externalId: string): void => {
    publishMessage({boardId, externalId, tabId: TAB_ID, type: 'restore'});
};

export const isBoardIdClaimedRemotely = (boardId: string): boolean => remoteClaims.has(boardId) && !localClaims.has(boardId);

export const subscribeProomTableCoordination = (
    handler: (message: ProomTableCoordinationMessage) => void
): (() => void) => {
    initChannel();
    subscribers.add(handler);

    return () => {
        subscribers.delete(handler);
    };
};
